import {
  ContentNugget,
  mapCareerPathResponseToCareerPath,
  Tool,
  collegeTransform,
  toolTransform,
} from '@backrs_ui/common-ui';

import {
  LubavResource,
  LubavCareerPath,
  LubavCollege,
  LubavCompetency,
  LubavInternship,
  LubavScholarship,
  LubavMajor,
  LubavIndustry,
  LubavEmployer,
  LubavSuggestedPlaybook,
  LubavCoach,
  LubavDeepDive,
  ResourceKey,
  RESOURCE_KEY,
  LubavTool,
} from '@backrs_ui/grow-ui/src/models/models';

export type LubavResourceTypes =
  | LubavCareerPath
  | LubavCollege
  | LubavCompetency
  | LubavInternship
  | LubavScholarship
  | LubavMajor
  | LubavIndustry
  | LubavEmployer
  | LubavSuggestedPlaybook
  | LubavResource
  | LubavTool
  | LubavCoach
  | LubavDeepDive;

// these nuggets need transform since they are not from content_nugget
export const nuggetsToMap: ResourceKey[] = [
  RESOURCE_KEY.TOOLS,
  RESOURCE_KEY.COLLEGES,
  RESOURCE_KEY.COMPETENCIES,
  RESOURCE_KEY.INTERNSHIPS,
  RESOURCE_KEY.SCHOLARSHIPS,
  RESOURCE_KEY.CAREER_PATHS,
  RESOURCE_KEY.MAJORS,
  RESOURCE_KEY.INDUSTRIES,
  RESOURCE_KEY.EMPLOYERS,
  RESOURCE_KEY.COACHES,
  RESOURCE_KEY.DEEP_DIVES,
  RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
];

export type MapResourceToContentNuggetProps = Readonly<{
  type: ResourceKey;
  resource: LubavResourceTypes;
}>;

export const mapResourceToContentNugget = ({
  type,
  resource,
}: MapResourceToContentNuggetProps): ContentNugget | null => {
  const baseNugget = {
    id: Number(resource.id) || 0,
    star_rating: 0,
    value_rating: 0,
    is_browsable: true,
  };

  switch (type) {
    case RESOURCE_KEY.TOOLS: {
      const tool = resource as LubavTool;

      if (!tool.id || !tool.metadata) {
        return null;
      }

      const toolMetadata = tool.metadata as Tool;

      return toolTransform(toolMetadata);
    }

    case RESOURCE_KEY.CAREER_PATHS: {
      const careerPath = resource as LubavCareerPath;

      if (!careerPath.metadata?.name) {
        return null;
      }

      return {
        ...baseNugget,
        slug: careerPath.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: careerPath.metadata.name,
        description: careerPath.metadata.description,
        variant: 'career-path',
        career_path_info: mapCareerPathResponseToCareerPath({
          ...careerPath.metadata,
        }),
      };
    }

    case RESOURCE_KEY.COLLEGES: {
      const college = resource as LubavCollege;

      if (!college.id || !college.metadata) {
        return null;
      }

      return collegeTransform(college.metadata);
    }

    case RESOURCE_KEY.COMPETENCIES: {
      const competency = resource as LubavCompetency;

      if (!competency.metadata?.slug || !competency.metadata?.title) {
        return null;
      }

      return {
        ...baseNugget,
        id: competency.metadata.id,
        slug: competency.metadata.slug,
        title: competency.metadata.title,
        description: competency.metadata.description,
        variant: 'competency',
        preview_media: competency.metadata.media_url
          ? { id: competency.metadata.id, media_url: competency.metadata.media_url }
          : undefined,
      };
    }

    case RESOURCE_KEY.INTERNSHIPS: {
      const internship = resource as LubavInternship;

      if (!internship.metadata?.role_title || !internship.metadata?.id) {
        return null;
      }

      return {
        ...baseNugget,
        title: internship.metadata.role_title,
        description: internship.metadata.ai_description,
        slug: internship.metadata.id.toString(),
        variant: 'internship',
        is_browsable: false,
        internship_info: internship.metadata,
        content_nugget_theme: 'Internship',
      };
    }

    case RESOURCE_KEY.SCHOLARSHIPS: {
      const scholarship = resource as LubavScholarship;

      if (!scholarship.metadata?.name) {
        return null;
      }

      return {
        ...baseNugget,
        slug: scholarship.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: scholarship.metadata.name,
        description: scholarship.metadata.description || '',
        variant: 'scholarships',
        scholarship_info: scholarship.metadata,
      };
    }

    case RESOURCE_KEY.MAJORS: {
      const major = resource as LubavMajor;

      if (!major.metadata?.name) {
        return null;
      }

      return {
        ...baseNugget,
        id: major.metadata.id,
        slug: major.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: major.metadata.name,
        description: major.metadata.description || '',
        variant: 'major',
        is_browsable: false,
        major_info: major.metadata,
      };
    }

    case RESOURCE_KEY.INDUSTRIES: {
      const industry = resource as LubavIndustry;

      if (!industry.metadata?.name) {
        return null;
      }

      return {
        ...baseNugget,
        slug: industry.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: industry.metadata.name,
        description: industry.metadata.description || '',
        variant: 'industry',
        is_browsable: false,
        industry_info: industry.metadata,
      };
    }

    case RESOURCE_KEY.EMPLOYERS: {
      const employer = resource as LubavEmployer;

      if (!employer.metadata?.name) {
        return null;
      }

      return {
        ...baseNugget,
        slug: employer.metadata.slug || employer.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: employer.metadata.name,
        description: employer.metadata.the_scoop_about || '',
        variant: 'employer',
        is_browsable: false,
        employer_info: employer.metadata,
      };
    }

    case RESOURCE_KEY.COACHES: {
      const coach = resource as LubavCoach;

      if (!coach.metadata?.first_name || !coach.metadata?.last_name) {
        return null;
      }

      const fullName = `${coach.metadata.first_name} ${coach.metadata.last_name}`;

      return {
        ...baseNugget,
        slug: fullName.toLowerCase().replace(/\s+/g, '-'),
        title: fullName,
        description: coach.metadata.headline || '',
        variant: 'coach',
        is_browsable: true,
        coach_info: coach.metadata,
      };
    }

    case RESOURCE_KEY.DEEP_DIVES: {
      const deepDive = resource as LubavDeepDive;

      if (!deepDive.metadata?.name) {
        return null;
      }

      return {
        ...baseNugget,
        slug: deepDive.metadata.name.toLowerCase().replace(/\s+/g, '-'),
        title: deepDive.metadata.name,
        description: deepDive.metadata.nugget_description || deepDive.metadata.description || '',
        variant: 'deep-dive',
        is_browsable: true,
        deep_dive_info: deepDive.metadata,
      };
    }

    case RESOURCE_KEY.SUGGESTED_PLAYBOOKS: {
      const playbook = resource as LubavSuggestedPlaybook;

      if (!playbook.metadata?.title) {
        return null;
      }

      const playlist = playbook.metadata;

      return {
        ...baseNugget,
        slug: playlist.slug || '',
        title: playlist.title,
        description: playlist.marketing_description || playlist.description || '',
        variant: 'playlist',
        is_browsable: true,
        playlist_info: {
          id: playlist.id,
          title: playlist.title,
          description: playlist.description,
          total_tasks: playlist.total_tasks,
          completed_tasks: playlist.completed_tasks,
          marketing_description: playlist.marketing_description,
          preview_media: playlist.preview_media,
        },
        preview_media: playlist.preview_media
          ? { id: 0, media_url: playlist.preview_media.media_url }
          : undefined,
      };
    }

    default: {
      return (resource as LubavResource).metadata || null;
    }
  }
};
